// Base-36 gives digits 0-9 + a-z, keeping the id short while
// remaining URL-safe for any downstream logging.
const BASE36_RADIX = 36;
// 8 base-36 characters = ~41 bits of entropy from `Math.random()` —
// more than enough uniqueness when combined with a millisecond
// timestamp within a single page-load scope.
const RANDOM_SUFFIX_START = 2;
const RANDOM_SUFFIX_END = 10;

/**
 * Generate a per-fire `eventId` for browser-only conversion events
 * that have no backend CAPI counterpart to dedup against (e.g.
 * `InitiateCheckout`, `ViewContent` on the paywall).
 *
 * Why this helper exists:
 * - `crypto.randomUUID()` only resolves on secure contexts (HTTPS or
 *   localhost) AND requires Chrome 92+ / Safari 15.4+ / Firefox 95+ /
 *   Edge 92+. On older browsers it throws `TypeError`; in an iframe
 *   without a secure context the property is simply absent.
 * - A thrown `TypeError` from the UUID call would bubble out of
 *   whichever effect invoked `fireConversion`, which in the checkout
 *   flow would break analytics event firing downstream.
 *
 * The fallback uses `Date.now()` + a `Math.random()` suffix. It is
 * **not** cryptographically unique, but is more than sufficient for
 * Meta dedup: these are browser-only intent events, so the id only
 * needs to be unique within the current page load to prevent a
 * double-fire dedup from collapsing two distinct user actions into
 * one. Even at 1 ms resolution the collision probability across a
 * single session is effectively zero.
 */
export default function generateConversionEventId(): string {
  const hasSecureRandomUuid = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function";
  if (hasSecureRandomUuid) {
    return crypto.randomUUID();
  }
  return `cs-${Date.now().toString(BASE36_RADIX)}-${Math.random().toString(BASE36_RADIX).slice(RANDOM_SUFFIX_START, RANDOM_SUFFIX_END)}`;
}
